import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { OnboardingDraft } from '@gql/shared-types';
import { api, ApiError } from '../api/client.js';
import { Stepper } from '../components/Stepper.js';
import { FileUpload, type UploadedFile } from '../components/FileUpload.js';

// Allgemeine Selbst-Registrierung (generisch) — GETRENNT vom fallspezifischen
// Fragebogen-Intake (siehe CaseIntake.tsx, Route /intake, nur per Einladung).

type Rechtsgebiet = NonNullable<OnboardingDraft['rechtsgebiet']>;

const RECHTSGEBIET_VALUES = [
  'arbeitsrecht', 'mietrecht', 'familienrecht', 'verkehrsrecht', 'strafrecht',
  'kartellrecht', 'vergaberecht', 'verwaltungsrecht', 'sonstiges',
] as const;

const STEPS = ['sachverhalt', 'gegenseite', 'vollmacht', 'uploads', 'review'] as const;
type StepId = (typeof STEPS)[number];

// `| undefined` ist nötig wegen tsconfig `exactOptionalPropertyTypes`.
interface Gegenseite {
  name: string;
  anschrift?: string | undefined;
  vertreten?: string | undefined;
}
interface DraftPayload {
  sachverhalt?: string | undefined;
  rechtsgebiet?: Rechtsgebiet | undefined;
  gegenseite?: Gegenseite | undefined;
  vollmachtErteilt?: boolean | undefined;
}

export function Onboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState<DraftPayload>({});
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getDraft();
        if (data) {
          const d = data.draft;
          setDraft({
            sachverhalt: d?.sachverhalt,
            rechtsgebiet: d?.rechtsgebiet,
            gegenseite: d?.gegenseite,
            vollmachtErteilt: d?.vollmachtErteilt,
          });
          setUploads(data.uploads ?? []);
          if (data.submitted) setSubmitted(true);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const saveDraft = useCallback((payload: DraftPayload) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.putDraft({ ...payload, uploads: [] } as OnboardingDraft)
        .catch((err) => console.error('[draft-save]', err));
    }, 600);
  }, []);

  const updateDraft = useCallback((patch: Partial<DraftPayload>) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      saveDraft(next);
      return next;
    });
  }, [saveDraft]);

  const handleUpload = async (files: File[], kategorie: string) => {
    setUploading(true);
    try {
      const res = await api.uploadFiles(files, kategorie);
      setUploads((prev) => [...prev, ...res]);
    } finally {
      setUploading(false);
    }
  };
  const handleRemove = async (id: string) => {
    await api.deleteUpload(id);
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const onSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.submit({
        sachverhalt: draft.sachverhalt!,
        rechtsgebiet: draft.rechtsgebiet!,
        ...(draft.gegenseite ? { gegenseite: draft.gegenseite } : {}),
        vollmachtErteilt: draft.vollmachtErteilt!,
        uploads: uploads.map((u) => ({
          id: u.id, filename: u.filename, mimetype: u.mimetype, size: u.size,
          kategorie: u.kategorie as 'ausweis' | 'vollmacht' | 'korrespondenz' | 'beweis' | 'sonstiges',
        })),
        bestaetigungVollstaendig: true,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.code === 'already_submitted' ? t('onboarding.errors.alreadySubmitted') : t('onboarding.errors.submitFailed'))
        : t('onboarding.errors.unexpected');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="card"><h2 className="card-title">{t('common.loading')}</h2></div>;
  }

  if (submitted) {
    return (
      <div className="card">
        <h2 className="card-title">{t('onboarding.submittedTitle')}</h2>
        <div className="alert alert-success">{t('onboarding.submittedBody')}</div>
        <p className="muted-block">
          {t('onboarding.submittedDashboardPrefix')}
          <a href="/dashboard" className="text-link">{t('onboarding.submittedDashboardLink')}</a>
          {t('onboarding.submittedDashboardSuffix')}
        </p>
      </div>
    );
  }

  const currentId: StepId = STEPS[stepIdx]!;

  return (
    <div className="card card-wide">
      <h2 className="card-title">{t('onboarding.title')}</h2>
      <p className="card-subtitle">{t('onboarding.subtitle')}</p>

      <Stepper steps={STEPS.map((id) => ({ id, label: t(`onboarding.steps.${id}`) }))} currentIndex={stepIdx} onStepClick={setStepIdx} />

      {currentId === 'sachverhalt' && <StepSachverhalt draft={draft} update={updateDraft} />}
      {currentId === 'gegenseite' && <StepGegenseite draft={draft} update={updateDraft} />}
      {currentId === 'vollmacht' && <StepVollmacht draft={draft} update={updateDraft} />}
      {currentId === 'uploads' && (
        <StepUploads uploads={uploads} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading} />
      )}
      {currentId === 'review' && (
        <StepReview draft={draft} uploads={uploads} submitting={submitting} error={error} onSubmit={onSubmit} />
      )}

      <div className="wizard-actions">
        <button type="button" className="button button-secondary"
          onClick={() => setStepIdx((i) => Math.max(0, i - 1))} disabled={stepIdx === 0}>
          {t('common.back')}
        </button>
        {stepIdx < STEPS.length - 1 && (
          <button type="button" className="button"
            onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
            disabled={!canAdvance(currentId, draft)}>
            {t('common.next')}
          </button>
        )}
      </div>
    </div>
  );
}

function canAdvance(step: StepId, d: DraftPayload): boolean {
  if (step === 'sachverhalt') return Boolean(d.sachverhalt && d.sachverhalt.trim().length >= 3 && d.rechtsgebiet);
  if (step === 'gegenseite') return Boolean(d.gegenseite?.name && d.gegenseite.name.trim().length >= 1);
  if (step === 'vollmacht') return d.vollmachtErteilt === true;
  return true;
}

function StepSachverhalt({ draft, update }: { draft: DraftPayload; update: (p: Partial<DraftPayload>) => void }) {
  const { t } = useTranslation();
  const chars = (draft.sachverhalt ?? '').length;
  const tooShort = (draft.sachverhalt ?? '').trim().length < 3;
  return (
    <div>
      <div className="field">
        <label className="field-label" htmlFor="rechtsgebiet">{t('onboarding.sachverhalt.rechtsgebietLabel')}</label>
        <select id="rechtsgebiet" className="field-input" value={draft.rechtsgebiet ?? ''}
          onChange={(e) => {
            const v = e.target.value as Rechtsgebiet | '';
            update(v === '' ? { rechtsgebiet: undefined } : { rechtsgebiet: v });
          }}>
          <option value="">{t('common.select')}</option>
          {RECHTSGEBIET_VALUES.map((r) => <option key={r} value={r}>{t(`rechtsgebiete.${r}`)}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="sachverhalt">{t('onboarding.sachverhalt.beschreibungLabel')}</label>
        <textarea id="sachverhalt" className="field-input field-textarea" rows={10} value={draft.sachverhalt ?? ''}
          onChange={(e) => update({ sachverhalt: e.target.value })}
          placeholder={t('onboarding.sachverhalt.beschreibungPlaceholder')} maxLength={20_000} />
        <div className="field-counter">
          {t('onboarding.sachverhalt.counter', { chars })}
          {tooShort && ` · ${t('onboarding.sachverhalt.minHint')}`}
        </div>
      </div>
    </div>
  );
}

function StepGegenseite({ draft, update }: { draft: DraftPayload; update: (p: Partial<DraftPayload>) => void }) {
  const { t } = useTranslation();
  const g = draft.gegenseite ?? { name: '' };
  const setG = (patch: Partial<typeof g>) => update({ gegenseite: { ...g, ...patch } });
  return (
    <div>
      <p className="hint">{t('onboarding.gegenseite.hint')}</p>
      <div className="field">
        <label className="field-label" htmlFor="g-name">{t('onboarding.gegenseite.nameLabel')}</label>
        <input id="g-name" className="field-input" value={g.name ?? ''} onChange={(e) => setG({ name: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="g-anschrift">{t('onboarding.gegenseite.anschriftLabel')}</label>
        <textarea id="g-anschrift" className="field-input field-textarea" rows={3} value={g.anschrift ?? ''}
          onChange={(e) => setG({ anschrift: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="g-vertreten">{t('onboarding.gegenseite.vertretenLabel')}</label>
        <input id="g-vertreten" className="field-input" value={g.vertreten ?? ''}
          onChange={(e) => setG({ vertreten: e.target.value })} />
      </div>
    </div>
  );
}

function StepVollmacht({ draft, update }: { draft: DraftPayload; update: (p: Partial<DraftPayload>) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <p>{t('onboarding.vollmacht.p1')}</p>
      <p>{t('onboarding.vollmacht.p2')}</p>
      <div className="checkbox-field">
        <input id="voll" type="checkbox" checked={draft.vollmachtErteilt ?? false}
          onChange={(e) => update({ vollmachtErteilt: e.target.checked })} />
        <label htmlFor="voll">{t('onboarding.vollmacht.checkbox')}</label>
      </div>
    </div>
  );
}

function StepUploads(props: {
  uploads: UploadedFile[];
  onUpload: (files: File[], kategorie: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  uploading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="hint">{t('onboarding.uploads.hint')}</p>
      <FileUpload {...props} />
    </div>
  );
}

function StepReview({
  draft, uploads, submitting, error, onSubmit,
}: {
  draft: DraftPayload; uploads: UploadedFile[]; submitting: boolean; error: string | null; onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const ready = Boolean(draft.sachverhalt && draft.rechtsgebiet && draft.gegenseite?.name && draft.vollmachtErteilt);
  return (
    <div>
      <h3 className="review-h">{t('onboarding.review.title')}</h3>
      {error && <div className="alert alert-error">{error}</div>}
      <dl className="review">
        <dt>{t('onboarding.review.rechtsgebiet')}</dt>
        <dd>{draft.rechtsgebiet ? t(`rechtsgebiete.${draft.rechtsgebiet}`) : t('common.dash')}</dd>
        <dt>{t('onboarding.review.sachverhalt')}</dt>
        <dd style={{ whiteSpace: 'pre-wrap' }}>{draft.sachverhalt ?? t('common.dash')}</dd>
        <dt>{t('onboarding.review.gegenseite')}</dt>
        <dd>
          {draft.gegenseite?.name ?? t('common.dash')}
          {draft.gegenseite?.anschrift && <><br />{draft.gegenseite.anschrift}</>}
          {draft.gegenseite?.vertreten && <><br /><em>{t('onboarding.review.vertretenDurch', { name: draft.gegenseite.vertreten })}</em></>}
        </dd>
        <dt>{t('onboarding.review.vollmachtErklaerung')}</dt>
        <dd>{draft.vollmachtErteilt ? t('common.yes') : t('common.dash')}</dd>
        <dt>{t('onboarding.review.unterlagen', { count: uploads.length })}</dt>
        <dd>{uploads.length === 0 ? t('common.dash') : <ul>{uploads.map((u) => <li key={u.id}>{u.filename}</li>)}</ul>}</dd>
      </dl>
      <button className="button" disabled={!ready || submitting} onClick={onSubmit}>
        {submitting ? t('common.submitting') : t('common.submit')}
      </button>
      {!ready && <p className="field-error" style={{ marginTop: 8 }}>{t('onboarding.review.notReady')}</p>}
    </div>
  );
}
